// Garder le lien avec les partenaires (D-125) : ce qui ne dépend que des données. La liste de ce que le club offre
// (propositions à cocher, aucune imposée), le trimestre et ses nouvelles, la saison des attentions, ce que l'app
// propose « ce mois-ci » (au moins un bon moment par trimestre pour chaque partenaire, au bon moment), le message
// d'invitation au vouvoiement qui suit ce qui est choisi, le brouillon des nouvelles (sans IA : les résultats, les
// temps forts, ce qui arrive). Rien d'argent : ni montant, ni contrat. D-147 : plus de messagerie du bureau (mailto) ;
// comment part une communication (l'e-mail du club, la notification), ce que le bureau voit de la réponse, la relance
// proposée, la promesse de don de l'app offerte. Logique pure, testée (lien.test.ts).

export type GiftKind = 'bon' | 'place' | 'presente' | 'photo' | 'merci' | 'objet' | 'autre';
export type PresetKey = 'boisson' | 'gaufre' | 'place' | 'presente' | 'photo' | 'merci' | 'maillot' | 'repas';

export interface Preset { key: PresetKey; title: string; sub: string; kind: GiftKind; isBon: boolean; short: string }
/** Les propositions de la liste (les mêmes que la base, _lien_preset), dans l'ordre de la décision. */
export const PRESETS: Preset[] = [
  { key: 'boisson', title: 'Boisson à la buvette', sub: 'Un bon dans l’espace du partenaire', kind: 'bon', isBon: true, short: 'boisson' },
  { key: 'gaufre', title: 'Gaufre ou crêpe', sub: 'Un bon dans l’espace du partenaire', kind: 'bon', isBon: true, short: 'gaufre' },
  { key: 'place', title: 'Place réservée en tribune', sub: 'Son nom sur la place, le soir du match', kind: 'place', isBon: false, short: 'place réservée' },
  { key: 'presente', title: 'Match présenté par le partenaire', sub: 'Son nom sur le match et la page du club', kind: 'presente', isBon: false, short: 'match présenté' },
  { key: 'photo', title: 'Photo avec l’équipe et une pancarte', sub: '« Merci » et son nom, partagée dans son espace', kind: 'photo', isBon: false, short: 'photo avec l’équipe' },
  { key: 'merci', title: 'Un merci au micro', sub: 'Une ligne pour la table de marque, à la mi-temps', kind: 'merci', isBon: false, short: 'merci au micro' },
  { key: 'maillot', title: 'Maillot dédicacé par l’équipe', sub: 'Remis en fin de saison', kind: 'objet', isBon: false, short: 'maillot dédicacé' },
  { key: 'repas', title: 'Repas d’après-match avec le bureau', sub: 'Au club-house, sur invitation', kind: 'objet', isBon: false, short: 'repas d’après-match' },
];

export interface Gift { id: string; preset: PresetKey | null; title: string; sub: string | null; kind: GiftKind; is_bon: boolean; active: boolean }
/** Une ligne de « Ce que le club offre » : une attention de la liste, ou une proposition encore jamais cochée. */
export interface GiftRow { id: string | null; preset: PresetKey | null; title: string; sub: string; kind: GiftKind; isBon: boolean; active: boolean }

/** La liste du club telle qu'on la montre : les propositions (cochées ou non), puis les attentions ajoutées par le club. */
export function giftRows(gifts: Gift[]): GiftRow[] {
  const byPreset = new Map(gifts.filter(g => g.preset).map(g => [g.preset!, g]));
  const presets = PRESETS.map(p => {
    const g = byPreset.get(p.key);
    return { id: g?.id ?? null, preset: p.key, title: g?.title ?? p.title, sub: g?.sub ?? p.sub, kind: p.kind, isBon: p.isBon, active: !!g?.active };
  });
  const own = gifts.filter(g => !g.preset).map(g => ({ id: g.id, preset: null, title: g.title, sub: g.sub ?? (g.is_bon ? 'Un bon dans l’espace du partenaire' : 'Offert par le club'),
    kind: g.kind, isBon: g.is_bon, active: g.active }));
  return [...presets, ...own];
}

/** Ce qu'une invitation peut offrir : les attentions allumées, sauf le merci au micro (une case à part). */
export const offerable = (gifts: Gift[]) => gifts.filter(g => g.active && g.kind !== 'merci');
export const micOn = (gifts: Gift[]) => gifts.some(g => g.active && g.kind === 'merci');

const lowerFirst = (s: string) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);
const upperFirst = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
/** « a, b et c » */
export function andList(items: string[]): string {
  const xs = items.filter(Boolean);
  return xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} et ${xs[xs.length - 1]}`;
}
/** Le mot court d'une attention (« boisson », « gaufre », « écharpe du club »). */
export function shortOf(g: { preset?: string | null; title: string }): string {
  return PRESETS.find(p => p.key === g.preset)?.short ?? lowerFirst(g.title);
}
/** Le titre du bon (« Boisson + gaufre »). */
export function bonTitle(gifts: { preset?: string | null; title: string; is_bon: boolean }[]): string {
  return upperFirst(gifts.filter(g => g.is_bon).map(shortOf).join(' + '));
}

// -----------------------------------------------------------------------------
// Dates : trimestres, saison, « il y a n jours »
// -----------------------------------------------------------------------------
const parse = (d: string) => { const [y, m, dd] = d.slice(0, 10).split('-').map(Number); return new Date(Date.UTC(y!, m! - 1, dd!)); };
export const daysBetween = (from: string, to: string) => Math.round((parse(to).getTime() - parse(from).getTime()) / 86_400_000);
const MONTHS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const MONTHS_LONG = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const DAYS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const DAYS_SHORT = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'];
/** « 17 août » */
export const dayMonth = (d: string) => { const t = parse(d); return `${t.getUTCDate() === 1 ? '1er' : t.getUTCDate()} ${MONTHS[t.getUTCMonth()]}`; };
/** « samedi 31 octobre » */
export const longDay = (d: string) => { const t = parse(d); return `${DAYS[t.getUTCDay()]} ${t.getUTCDate() === 1 ? '1er' : t.getUTCDate()} ${MONTHS_LONG[t.getUTCMonth()]}`; };
/** « sam. 31 oct. » */
export const shortDay = (d: string) => { const t = parse(d); return `${DAYS_SHORT[t.getUTCDay()]} ${t.getUTCDate() === 1 ? '1er' : t.getUTCDate()} ${MONTHS[t.getUTCMonth()]}`; };

/** « dernier geste il y a 74 jours » */
export function lastGestureText(last: string | null | undefined, today: string): string {
  if (!last) return 'aucun geste encore';
  const n = daysBetween(last, today);
  return n <= 0 ? 'dernier geste aujourd’hui' : n === 1 ? 'dernier geste hier' : `dernier geste il y a ${n} jours`;
}

export interface Quarter { key: string; q: 1 | 2 | 3 | 4; year: number; name: string; from: string; to: string; lastMonth: boolean }
const Q_NAME = { 1: 'de l’hiver', 2: 'du printemps', 3: 'de l’été', 4: 'de l’automne' } as const;
/** Le trimestre d'une date (« 2026-T4 », « de l’automne », du 1er octobre au 31 décembre). */
export function quarterOf(d: string): Quarter {
  const t = parse(d), y = t.getUTCFullYear(), m = t.getUTCMonth() + 1;
  const q = (Math.floor((m - 1) / 3) + 1) as 1 | 2 | 3 | 4;
  const fm = (q - 1) * 3 + 1, lm = fm + 2;
  const last = new Date(Date.UTC(y, lm, 0)).getUTCDate();
  const pad = (n: number) => String(n).padStart(2, '0');
  return { key: `${y}-T${q}`, q, year: y, name: Q_NAME[q], from: `${y}-${pad(fm)}-01`, to: `${y}-${pad(lm)}-${pad(last)}`, lastMonth: m === lm };
}
export const newsTitle = (q: Quarter) => `Les nouvelles ${q.name}`;
/** La saison (1er juillet – 30 juin, D-115) : l'année de son début. */
export const seasonStartYear = (d: string) => { const t = parse(d); return t.getUTCMonth() + 1 >= 7 ? t.getUTCFullYear() : t.getUTCFullYear() - 1; };

// -----------------------------------------------------------------------------
// La saison des attentions
// -----------------------------------------------------------------------------
export type PlanKind = 'invite' | 'news' | 'voeux' | 'fin';
export interface PlanRow { key: string; months: string; title: string; kind: PlanKind; from: string; to: string; quarter?: string }
/** Les rendez-vous de la saison qui commence l'année `y` : nouvelles chaque trimestre, deux fenêtres d'invitations,
 *  les vœux en janvier, la fin de saison en juin. */
export function seasonPlan(y: number): PlanRow[] {
  const n = y + 1;
  return [
    { key: 'news-ete', months: 'Sept.', title: 'Nouvelles de l’été', kind: 'news', from: `${y}-09-01`, to: `${y}-09-30`, quarter: `${y}-T3` },
    { key: 'inv-automne', months: 'Oct.–nov.', title: 'Une invitation à un match pour chacun', kind: 'invite', from: `${y}-10-01`, to: `${y}-11-30` },
    { key: 'news-automne', months: 'Déc.', title: 'Nouvelles de l’automne', kind: 'news', from: `${y}-12-01`, to: `${y}-12-31`, quarter: `${y}-T4` },
    { key: 'voeux', months: 'Janv.', title: 'Carte de vœux des équipes', kind: 'voeux', from: `${n}-01-01`, to: `${n}-01-31` },
    { key: 'inv-hiver', months: 'Févr.–mars', title: 'Une invitation à un match pour chacun', kind: 'invite', from: `${n}-02-01`, to: `${n}-03-31` },
    { key: 'news-hiver', months: 'Mars', title: 'Nouvelles de l’hiver', kind: 'news', from: `${n}-03-01`, to: `${n}-03-31`, quarter: `${n}-T1` },
    { key: 'fin', months: 'Juin', title: 'Photo d’équipe signée, fête du club', kind: 'fin', from: `${n}-05-15`, to: `${n}-06-30` },
  ];
}

// -----------------------------------------------------------------------------
// Ce que reçoit l'écran (lien_board)
// -----------------------------------------------------------------------------
export interface BoardPartner {
  id: string; name: string; level: string | null; kind: string; since_year: number | null; logo_url: string | null;
  contact_name: string | null; civility: 'Madame' | 'Monsieur' | null; email: string | null; restricted: boolean; has_account: boolean;
  last: { on: string; kind: string; title: string } | null;
}
export interface BoardMatch {
  event_id: string; title: string; starts_at: string; day: string; time: string; location: string | null; category: string | null;
  presented_by: string | null; presented_name: string | null; invited: number;
}
export type InviteStatus = 'envoyee' | 'vient' | 'pas_cette_fois' | 'annulee';
export interface InvitedGift { id: string; title: string; kind: GiftKind; is_bon: boolean; preset: PresetKey | null }
export interface BoardInvitation {
  id: string; partner_id: string; event_id: string; title: string; starts_at: string; day: string; status: InviteStatus; via: 'app' | 'messagerie' | 'email' | 'lien';
  created_on: string; host_name: string | null; gifts: InvitedGift[]; thanks_mic: boolean; photo: boolean;
}
export interface BoardNews { id: string; quarter: string; title: string; body: string; photo_ids: string[]; status: 'brouillon' | 'publiee'; published_at: string | null; seen: number; sent: number }
export interface Board {
  today: string; demo: boolean; partners: BoardPartner[]; gifts: Gift[]; matches: BoardMatch[]; invitations: BoardInvitation[]; news: BoardNews[];
  collective: Partial<Record<'voeux' | 'merci' | 'fin_saison', string>>;
  moments: { event_id: string; title: string; us: number; them: number; day: string }[];
  hosts: { user_id: string; name: string }[];
}

export const STATUS_TEXT: Record<InviteStatus, string> = { envoyee: 'en attente de sa réponse', vient: 'vient', pas_cette_fois: 'pas cette fois', annulee: 'annulée' };

/** Une invitation à venir (son match n'est pas passé). */
export const openInvitation = (b: Board, partnerId: string) =>
  b.invitations.find(i => i.partner_id === partnerId && i.status !== 'annulee' && i.day >= b.today) ?? null;

/** Le partenaire a-t-il eu son invitation dans la fenêtre en cours ? */
const invitedIn = (b: Board, partnerId: string, from: string, to: string) =>
  b.invitations.some(i => i.partner_id === partnerId && i.status !== 'annulee' && i.created_on >= from && i.created_on <= to);

export interface PlanState { row: PlanRow; state: 'fait' | 'en cours' | 'prêtes' | 'à préparer' | 'à venir'; done?: number; total?: number }
/** La saison des attentions, à partir du rendez-vous en cours. */
export function seasonState(b: Board): PlanState[] {
  const rows = seasonPlan(seasonStartYear(b.today));
  const n = b.partners.length;
  return rows.filter(r => r.to >= b.today).map(r => {
    if (r.kind === 'invite') {
      const done = b.partners.filter(p => invitedIn(b, p.id, r.from, r.to) || (r.from <= b.today && !!openInvitation(b, p.id))).length;
      return { row: r, state: r.from > b.today ? 'à venir' : 'en cours', done, total: n };
    }
    if (r.kind === 'news') {
      const nw = b.news.find(x => x.quarter === r.quarter);
      return { row: r, state: nw?.status === 'publiee' ? 'fait' : nw ? 'prêtes' : r.from <= b.today ? 'à préparer' : 'à venir' };
    }
    const last = r.kind === 'voeux' ? b.collective.voeux : b.collective.fin_saison;
    return { row: r, state: last && last >= r.from && last <= r.to ? 'fait' : r.from <= b.today ? 'à préparer' : 'à venir' };
  });
}

// -----------------------------------------------------------------------------
// « Ce mois-ci » : ce que l'app propose (rien ne part sans le bureau)
// -----------------------------------------------------------------------------
export type MonthItem =
  | { kind: 'invite'; partner: BoardPartner; match: BoardMatch; last: string | null }
  | { kind: 'invited'; partner: BoardPartner; invitation: BoardInvitation }
  | { kind: 'news'; quarter: Quarter; draft: BoardNews | null }
  | { kind: 'voeux' }
  | { kind: 'merci'; moment: Board['moments'][number] }
  | { kind: 'fin' };

/** Trois invitations proposées au plus à la fois ; deux partenaires au plus par match ; un match dans les 3 semaines. */
export const MAX_PROPOSALS = 3;
const PER_MATCH = 2;
const SOON_DAYS = 21;
/** Un partenaire est « dû » : pas d'invitation depuis 75 jours et pas de geste depuis 45 jours (ou jamais). Au fil de la
 *  saison (septembre – mai), cela fait une invitation par trimestre environ, en plus des nouvelles. */
export const INVITE_EVERY = 75;
export const QUIET_DAYS = 45;

/** Les invitations proposées : les partenaires dus, le plus ancien dernier geste d'abord, chacun à un match à domicile
 *  proche (deux au plus par match). */
export function inviteProposals(b: Board): { partner: BoardPartner; match: BoardMatch }[] {
  // Les matchs des équipes adultes d'abord (le soir, en championnat : le moment qu'on partage), puis le plus proche.
  const youth = (m: BoardMatch) => /^U\s?\d/i.test(m.category ?? '');
  const soon = b.matches.filter(m => m.day >= b.today && daysBetween(b.today, m.day) <= SOON_DAYS)
    .sort((x, y) => Number(youth(x)) - Number(youth(y)) || x.starts_at.localeCompare(y.starts_at));
  if (!soon.length) return [];
  const load = new Map(soon.map(m => [m.event_id, m.invited]));
  const recent = (p: BoardPartner) => b.invitations.some(i => i.partner_id === p.id && i.status !== 'annulee' && daysBetween(i.created_on, b.today) < INVITE_EVERY);
  const due = b.partners.filter(p => !openInvitation(b, p.id) && !recent(p) && (!p.last || daysBetween(p.last.on, b.today) >= QUIET_DAYS))
    .sort((a, c) => (a.last?.on ?? '0000').localeCompare(c.last?.on ?? '0000') || a.name.localeCompare(c.name, 'fr'));
  const out: { partner: BoardPartner; match: BoardMatch }[] = [];
  for (const p of due) {
    if (out.length >= MAX_PROPOSALS) break;
    const m = soon.find(x => (load.get(x.event_id) ?? 0) < PER_MATCH);
    if (!m) break;
    load.set(m.event_id, (load.get(m.event_id) ?? 0) + 1);
    out.push({ partner: p, match: m });
  }
  return out;
}

export function monthItems(b: Board): MonthItem[] {
  const items: MonthItem[] = [];
  for (const x of inviteProposals(b)) items.push({ kind: 'invite', partner: x.partner, match: x.match, last: x.partner.last?.on ?? null });
  for (const i of b.invitations.filter(i => i.status !== 'annulee' && i.day >= b.today && daysBetween(b.today, i.day) <= 31)) {
    const p = b.partners.find(x => x.id === i.partner_id);
    if (p) items.push({ kind: 'invited', partner: p, invitation: i });
  }
  const q = quarterOf(b.today);
  const draft = b.news.find(n => n.quarter === q.key) ?? null;
  // Juin : la fin de saison tient lieu de nouvelles du printemps.
  if (b.partners.length && draft?.status !== 'publiee' && (draft || (q.lastMonth && q.q !== 2))) items.push({ kind: 'news', quarter: q, draft });
  const month = b.today.slice(5, 7);
  if (b.partners.length && month === '01' && !(b.collective.voeux && b.collective.voeux >= `${b.today.slice(0, 4)}-01-01`)) items.push({ kind: 'voeux' });
  const m = b.moments.find(x => !(b.collective.merci && b.collective.merci >= x.day));
  if (b.partners.length && m) items.push({ kind: 'merci', moment: m });
  const fin = seasonPlan(seasonStartYear(b.today)).find(r => r.kind === 'fin')!;
  if (b.partners.length && b.today >= fin.from && b.today <= fin.to && !(b.collective.fin_saison && b.collective.fin_saison >= fin.from)) items.push({ kind: 'fin' });
  return items;
}

/** Ce qui demande un geste ce mois-ci : sans les invitations déjà envoyées (elles attendent la réponse du partenaire).
 *  Le même compteur sur l'accueil, la page Partenaires et « Garder le lien » (B4). */
export const monthTodo = (b: Board): MonthItem[] => monthItems(b).filter(i => i.kind !== 'invited');

/** La proposition du moment pour un partenaire (sa fiche) : l'inviter, s'il est dû. */
export const proposalFor = (b: Board, partnerId: string) => inviteProposals(b).find(x => x.partner.id === partnerId) ?? null;

// -----------------------------------------------------------------------------
// Le message d'invitation, au vouvoiement (il suit ce qui est choisi)
// -----------------------------------------------------------------------------
/** « Madame Martin » (ou le nom tel quel, ou rien). */
export function greeting(civility: string | null | undefined, contact: string | null | undefined): string {
  const name = (contact ?? '').trim();
  if (!name) return '';
  const last = name.split(/\s+/).slice(-1)[0]!;
  return civility ? `${civility} ${last}` : name;
}
/** « Accueillie par » (Madame), « Accueilli par » (Monsieur), sinon « Qui l’accueille ». */
export const hostLabel = (civility: string | null | undefined) =>
  civility === 'Madame' ? 'Accueillie par' : civility === 'Monsieur' ? 'Accueilli par' : 'Qui l’accueille';

/** « l’ASC Valdorne », « le BC Tavernel », « Valbrenne ». */
export function withArticle(club: string): string {
  const c = club.trim();
  if (/^(basket|club|cercle|stade|racing|foyer|bc|cb|sporting)\b/i.test(c)) return `le ${c}`;
  if (/^(as|us|es|asc|ascl|uscl|ab|union|association|entente|étoile|elan|élan|avenir|alliance)\b/i.test(c)) return /^[aeiouyhàâéèêëîïôûü]/i.test(c) ? `l’${c}` : `la ${c}`;
  return c;
}
/** « au gymnase de Rivebelle », « à la salle Bellevue », « à Valbrenne ». */
export function atPlace(loc: string | null | undefined): string {
  const l = (loc ?? '').trim();
  if (!l) return '';
  if (/^(gymnase|complexe|palais|stade|dojo|centre)\b/i.test(l)) return `au ${lowerFirst(l)}`;
  if (/^(salle|halle|maison)\b/i.test(l)) return `à la ${lowerFirst(l)}`;
  return `à ${l}`;
}
/** « SF1 – ASC Valdorne » → l'équipe et l'adversaire. */
export function splitTitle(title: string): { team: string; opponent: string } {
  const i = title.indexOf(' – ');
  return i < 0 ? { team: '', opponent: title } : { team: title.slice(0, i), opponent: title.slice(i + 3) };
}

export interface InviteChoice {
  partnerName: string; civility: string | null; contact: string | null;
  match: { title: string; day: string; time: string; location: string | null };
  host: string | null; gifts: { title: string; kind: GiftKind; preset: PresetKey | null; is_bon: boolean }[]; mic: boolean;
}
const BUVETTE: Partial<Record<PresetKey, string>> = { boisson: 'une boisson', gaufre: 'une gaufre' };

/** Le message prêt, au vouvoiement, qui suit le match, l'accueil et les attentions choisis. */
export function inviteMessage(c: InviteChoice): string {
  const { team, opponent } = splitTitle(c.match.title);
  const g = greeting(c.civility, c.contact);
  const lieu = atPlace(c.match.location);
  const s1 = `Bonjour${g ? ` ${g}` : ''}, ${team ? `les ${team} reçoivent ${withArticle(opponent)}` : `le club reçoit ${withArticle(opponent)}`} ${longDay(c.match.day)} à ${c.match.time}${lieu ? ` ${lieu}` : ''}.`;
  const buvette = c.gifts.filter(x => x.preset && BUVETTE[x.preset]).map(x => BUVETTE[x.preset!]!);
  const parts: string[] = [];
  if (c.host) parts.push(`${c.host} vous attendra à l’entrée`);
  if (buvette.length) parts.push(`${andList(buvette)} vous ${buvette.length > 1 ? 'attendent' : 'attend'} à la buvette`);
  const s2 = `Nous serions ravis de vous y accueillir${parts.length ? ` : ${parts.join(', et ')}` : ''}.`;
  const more: string[] = [];
  for (const x of c.gifts) {
    if (x.preset && BUVETTE[x.preset]) continue;
    if (x.preset === 'place') more.push('une place vous est réservée en tribune');
    else if (x.preset === 'presente' || x.kind === 'presente') more.push(`le match sera présenté par ${c.partnerName}`);
    else if (x.preset === 'photo' || x.kind === 'photo') more.push(`nous aimerions faire une photo avec l’équipe et une pancarte « Merci ${c.partnerName} »`);
    else if (x.preset === 'maillot') more.push('un maillot dédicacé par l’équipe vous sera remis');
    else if (x.preset === 'repas') more.push('nous vous invitons aussi au repas d’après-match avec le bureau');
    else more.push(`le club vous offre aussi : ${lowerFirst(x.title)}`);
  }
  if (c.mic) more.push('nous vous remercierons au micro à la mi-temps');
  const s3 = more.map(x => `${upperFirst(x)}.`).join(' ');
  return [s1, s2, s3, 'Pouvez-vous nous dire si vous venez ?'].filter(Boolean).join(' ');
}

/** Le lien de la page de réponse, à partager par WhatsApp ou SMS (D-147) : le message, puis le lien. */
export function inviteShareText(c: InviteChoice, link: string): string {
  return `${inviteMessage(c)} Un clic suffit, sans compte : ${link}`;
}

// -----------------------------------------------------------------------------
// D-147 : comment part une communication, et ce que le bureau voit ensuite
// -----------------------------------------------------------------------------
/** Ce que la base sait d'un partenaire pour lui écrire (lien_mail_reach). */
export interface ReachPartner { id: string; name: string; email: string | null; has_account: boolean; app_only: boolean; stopped: boolean; to: string | null }
export type MailWhy = 'ok' | 'none' | 'app_only' | 'stopped';
/** Comment part une communication à ce partenaire : l'e-mail (son adresse ou pourquoi pas), la notification (son compte). */
export function waysOf(p: ReachPartner | null | undefined): { email: string | null; why: MailWhy; app: boolean } {
  if (!p) return { email: null, why: 'none', app: false };
  const why: MailWhy = p.to ? 'ok' : p.stopped && p.email ? 'stopped' : p.app_only && p.has_account && p.email ? 'app_only' : 'none';
  return { email: p.to, why, app: p.has_account };
}
/** « Comment ça part » des nouvelles, des vœux… : combien par e-mail, combien dans leur espace, qui n'a pas d'e-mail. */
export function reachCount(list: ReachPartner[]): { mail: number; app: ReachPartner[]; without: ReachPartner[] } {
  return { mail: list.filter(p => p.to).length, app: list.filter(p => p.has_account), without: list.filter(p => !p.to && !p.has_account) };
}

/** Ce que le bureau voit d'une invitation (lien_followup). */
export interface FollowInvite {
  id: string; partner_id: string; partner: string; logo: string | null; event_id: string; title: string; day: string; time: string;
  status: InviteStatus; via: 'app' | 'messagerie' | 'email' | 'lien'; mail_to: string | null; notified: boolean; sent_at: string;
  opened_at: string | null; answered_at: string | null; answered_via: 'page' | 'app' | 'bureau' | null; answer_note: string | null;
  reminded_at: string | null; host_name: string | null; gifts: InvitedGift[]; thanks_mic: boolean; token: string; has_account: boolean;
  mail_status: 'queued' | 'sending' | 'sent' | 'failed' | 'skipped' | null; can_remind: boolean;
}

const pad2 = (n: number) => String(n).padStart(2, '0');
const localDay = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
/** « 9h12 » (heure de l'appareil). */
export const hourOf = (iso: string) => { const d = new Date(iso); return `${d.getHours()}h${pad2(d.getMinutes())}`; };
/** « aujourd’hui à 9h12 », « hier à 18h40 », « le 31 oct. à 18h40 ». */
export function whenText(iso: string, now: Date = new Date()): string {
  const d = new Date(iso), day = localDay(d), today = localDay(now);
  const n = daysBetween(day, today);
  return `${n === 0 ? 'aujourd’hui' : n === 1 ? 'hier' : `le ${dayMonth(day)}`} à ${hourOf(iso)}`;
}
/** « ce matin », « cet après-midi », « ce soir », « hier », « le 31 oct. ». */
export function momentText(iso: string, now: Date = new Date()): string {
  const d = new Date(iso), n = daysBetween(localDay(d), localDay(now));
  if (n === 0) return d.getHours() < 12 ? 'ce matin' : d.getHours() < 18 ? 'cet après-midi' : 'ce soir';
  return n === 1 ? 'hier' : `le ${dayMonth(localDay(d))}`;
}
/** Comment elle est partie : « par e-mail », « dans son espace et par e-mail », « dans son espace », « par le lien ». */
export function sentHow(i: Pick<FollowInvite, 'via' | 'mail_to' | 'notified'>): string {
  if (i.via === 'email') return i.notified ? 'dans son espace et par e-mail' : 'par e-mail';
  if (i.via === 'app') return 'dans son espace';
  if (i.via === 'lien') return 'par le lien (WhatsApp ou SMS)';
  return 'depuis ta messagerie';
}
/** « Envoyée par e-mail hier à 18h40, ouverte ce matin. » */
export function sentLine(i: FollowInvite, now: Date = new Date()): string {
  const how = sentHow(i);
  return `${upperFirst(`envoyée ${how} ${whenText(i.sent_at, now)}`)}${i.opened_at ? `, ouverte ${momentText(i.opened_at, now)}` : ''}${i.reminded_at ? ` ; relancée ${momentText(i.reminded_at, now)}` : ''}.`;
}
/** La réponse : « réponse par e-mail, aujourd’hui à 9h12 » ; « pas encore de réponse ». */
export function answerLine(i: FollowInvite, now: Date = new Date()): string {
  if (i.status === 'envoyee') return 'pas encore de réponse';
  const by = i.answered_via === 'page' ? (i.via === 'email' ? 'réponse par e-mail' : 'réponse par le lien')
    : i.answered_via === 'app' ? 'réponse dans son espace' : i.answered_via === 'bureau' ? 'réponse notée par le bureau' : 'réponse';
  return i.answered_at ? `${by}, ${whenText(i.answered_at, now)}` : by;
}
/** La pastille : « Vient », « Ne vient pas », « Sans réponse ». */
export function guestChip(i: Pick<FollowInvite, 'status'>): { text: string; cls: 'chip-ok' | 'chip-mute' | 'chip-warn' } {
  return i.status === 'vient' ? { text: 'Vient', cls: 'chip-ok' } : i.status === 'pas_cette_fois' ? { text: 'Ne vient pas', cls: 'chip-mute' } : { text: 'Sans réponse', cls: 'chip-warn' };
}
/** « il y a 3 jours » (depuis l'envoi). */
export function sinceText(iso: string, now: Date = new Date()): string {
  const n = daysBetween(localDay(new Date(iso)), localDay(now));
  return n <= 0 ? 'aujourd’hui' : n === 1 ? 'hier' : `il y a ${n} jours`;
}
/** Une relance se propose 3 jours après l'envoi, une fois, sans réponse (la base le vérifie aussi). */
export const REMIND_AFTER_DAYS = 3;

// -----------------------------------------------------------------------------
// D-147 : l'app offerte par un partenaire (la promesse de don)
// -----------------------------------------------------------------------------
/** Non réglée après 60 jours : le bureau peut relancer ou retirer la ligne. */
export const PLEDGE_DUE_DAYS = 60;
export const pledgeLate = (pledgedAt: string | null | undefined, today: string): boolean =>
  !!pledgedAt && daysBetween(pledgedAt.slice(0, 10), today) >= PLEDGE_DUE_DAYS;

/** « Samedi 31 octobre · 20h30 · boisson et gaufre · accueil par un membre du bureau » (la proposition sur la fiche). */
export function proposalLine(m: { day: string; time: string }, gifts: Gift[]): string {
  const offered = offerable(gifts).filter(g => g.is_bon).map(shortOf);
  return [upperFirst(longDay(m.day)), m.time, offered.length ? andList(offered) : '', 'accueil par un membre du bureau'].filter(Boolean).join(' · ');
}

// -----------------------------------------------------------------------------
// Les nouvelles du club : le brouillon du trimestre (composé tout seul, relu par le bureau)
// -----------------------------------------------------------------------------
export interface NewsInputs {
  results: { team: string; group: string; category: string | null; opponent: string; is_home: boolean | null; us: number | null; them: number | null; forfeit: 'us' | 'them' | null; day: string }[];
  photos: { id: string; caption: string | null; match: string; day: string }[];
  upcoming: { title: string; day: string }[];
}
export interface NewsDraft { title: string; body: string; photoIds: string[]; summary: string }

const outcome = (r: NewsInputs['results'][number]) =>
  r.forfeit === 'them' ? 'w' : r.forfeit === 'us' ? 'l' : r.us == null || r.them == null ? null : r.us > r.them ? 'w' : r.us < r.them ? 'l' : 'd';
const count = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;
/** « deux photos », « une photo ». */
const photoWord = (n: number) => (n >= 2 ? 'deux photos' : n === 1 ? 'une photo' : '');

/** Le brouillon : les résultats par équipe, les temps forts (les belles victoires), ce qui arrive, deux photos. */
export function composeNews(inp: NewsInputs, q: Quarter, clubName: string): NewsDraft {
  const teams = new Map<string, { w: number; l: number; d: number }>();
  for (const r of inp.results) {
    const o = outcome(r);
    if (!o) continue;
    const t = teams.get(r.team) ?? { w: 0, l: 0, d: 0 };
    t[o]++;
    teams.set(r.team, t);
  }
  const lines = [...teams].map(([team, t]) => `• ${team} : ${[t.w ? count(t.w, 'victoire', 'victoires') : '', t.l ? count(t.l, 'défaite', 'défaites') : '', t.d ? count(t.d, 'match nul', 'matchs nuls') : ''].filter(Boolean).join(', ')}`);
  const big = inp.results.filter(r => outcome(r) === 'w' && r.us != null && r.them != null && r.us - r.them >= 15)
    .sort((a, c) => (c.us! - c.them!) - (a.us! - a.them!)).slice(0, 2)
    .map(r => `la victoire des ${r.team} ${r.is_home === false ? 'à' : 'contre'} ${r.opponent} (${r.us}–${r.them})`);
  const upcoming = inp.upcoming.slice(0, 3).map(e => `${e.title} le ${longDay(e.day)}`);
  const photos = inp.photos.slice(0, 2).map(p => p.id);
  const body = [
    'Bonjour,',
    `Voici les nouvelles du ${clubName} ce trimestre.`,
    lines.length ? `Les résultats :\n${lines.join('\n')}` : '',
    big.length ? `Les temps forts : ${big.join(' ; ')}.` : '',
    upcoming.length ? `À venir : ${upcoming.join(' ; ')}. Vous êtes les bienvenus.` : '',
    'Merci d’être à nos côtés.',
    `Le bureau du ${clubName}`,
  ].filter(Boolean).join('\n\n');
  return { title: newsTitle(q), body, photoIds: photos, summary: newsSummary(body, photos.length) };
}

/** « résultats, la victoire des SF1 contre Crestavel, deux photos » : ce que contient un brouillon. */
export function newsSummary(body: string, photos: number): string {
  const parts: string[] = [];
  if (/^Les résultats\b/m.test(body)) parts.push('résultats');
  const strong = /^Les temps forts\s*:\s*([^;.\n]+)/m.exec(body);
  if (strong) parts.push(strong[1]!.trim());
  if (/^À venir\b/m.test(body)) parts.push('ce qui arrive');
  if (photos) parts.push(photoWord(photos));
  return parts.join(', ');
}

/** Rien d'argent dans le fil : un texte qui parle d'euros est refusé (la base le refuse aussi). */
export const hasMoney = (s: string) => /€|\beuros?\b/i.test(s);
